import { useMemo, useState } from 'react';

import { apiClient } from '@infrastructure/api/client';
import { AdminListRepository } from '@infrastructure/api/AdminListRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { TabbedListSection } from '@app/components/TabbedListSection';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

interface ConsentRow {
  id: number;
  contact_phone: string;
  customer_email: string;
  customer_company: string;
  channel: string;
  action: string;
  ts: string;
  proof: Record<string, unknown>;
}

interface TemplateRow {
  id: number;
  customer_email: string;
  name: string;
  slug: string;
  kind: string;
  status: string;
  created_at: string;
}

const consentRepo = new AdminListRepository<ConsentRow>(apiClient, 'consents');
const templateRepo = new AdminListRepository<TemplateRow>(apiClient, 'compliance-templates');

function ConsentTab() {
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (actionFilter) f.action = actionFilter;
    if (search) f.search = search;
    return f;
  }, [actionFilter, search]);

  const cols: DataTableColumn<ConsentRow>[] = [
    { id: 'ts', header: 'When', accessor: (r) => formatDateTime(r.ts) },
    { id: 'contact_phone', header: 'Phone', accessor: (r) => <code className="font-mono text-xs">{r.contact_phone}</code> },
    {
      id: 'customer_email', header: 'Customer',
      accessor: (r) => (
        <span>
          {r.customer_company}
          <span className="block text-xs text-slate-500">{r.customer_email}</span>
        </span>
      ),
    },
    { id: 'channel', header: 'Channel', accessor: 'channel' },
    {
      id: 'action', header: 'Action',
      accessor: (r) => (
        <span className={r.action === 'opt_in' ? 'text-green-700' : 'text-red-700'}>{r.action}</span>
      ),
    },
    {
      id: 'proof', header: 'Proof',
      accessor: (r) => (
        <code className="font-mono text-xs text-slate-600">
          {Object.keys(r.proof || {}).length ? JSON.stringify(r.proof).slice(0, 80) : '—'}
        </code>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <p className="text-xs text-slate-500">
          STOP/START replies to platform numbers. The opt-out itself is on the contact (Contacts page).
        </p>
        <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search phone or customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-2 py-1"
        />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">All actions</option>
          <option value="opt_in">Opt-in</option>
          <option value="opt_out">Opt-out</option>
        </select>
        </div>
      </div>
      <DataTable<ConsentRow>
        columns={cols}
        fetcher={(p) => consentRepo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={50}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No consent events.</span>}
      />
    </div>
  );
}

function TemplatesTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const filters = useMemo<Record<string, string>>(() => {
    const f: Record<string, string> = {};
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [statusFilter]);

  const cols: DataTableColumn<TemplateRow>[] = [
    { id: 'name', header: 'Name', accessor: 'name' },
    { id: 'customer_email', header: 'Customer', accessor: 'customer_email' },
    { id: 'kind', header: 'Kind', accessor: 'kind' },
    {
      id: 'status', header: 'Status',
      accessor: (r) => <StatusBadge value={r.status} />,
    },
    { id: 'created_at', header: 'Created', accessor: (r) => formatDateTime(r.created_at) },
  ];

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <p className="text-xs text-slate-500">
          Automatic policy check of templates sent to the templates/create API. Nothing requires approval to send.
        </p>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <DataTable<TemplateRow>
        columns={cols}
        fetcher={(p) => templateRepo.list({ ...p, filters })}
        pagination="offset"
        defaultPageSize={25}
        rowKey={(r) => String(r.id)}
        emptyState={<span data-testid="datatable-empty">No compliance templates.</span>}
      />
    </div>
  );
}

export function CompliancePage() {
  return (
    <TabbedListSection
      title="Compliance"
      tabs={[
        { id: 'consents', label: 'Consents', render: () => <ConsentTab /> },
        { id: 'templates', label: 'Templates', render: () => <TemplatesTab /> },
      ]}
    />
  );
}
